import {
  InputAdornment,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  borderColors,
  ColorsConstants,
  inputColor,
} from "../cssUtils/constants";
import { ReactComponent as Search } from "../assets/images/icons/search/search.svg";
import typography from "../cssUtils/typography";

/**
 * TableSearchInput
 *
 * Props:
 *  - searchQuery    {string}    Controlled value
 *  - onSearchChange {Function}  (event) => void
 *  - tooltip        {string}    Tooltip text on hover
 *  - placeholder    {string}    Input placeholder (default: "Search")
 *  - charCount      {number}    Optional — shows "N/maxChars" counter in end adornment
 *  - maxChars       {number}    Max char count shown in counter (default: 50)
 *  - loading        {boolean}   Shows a spinner in the end adornment (replaces charCount)
 *  - disabled       {boolean}   Disables the input
 *  - onKeyPress     {Function}  (event) => void — e.g. trigger search on Enter
 *  - fullWidth      {boolean}   Default: true
 *  - error          {boolean}   MUI error state on the field
 *  - helperText     {string}    Inline validation (e.g. phone search)
 *  - clearable      {boolean}   Shows a clear (X) button while there is text; clearing calls
 *                               onSearchChange with { target: { value: "" } }. Default: false
 */
const TableSearchInput = ({
  searchQuery,
  onSearchChange,
  tooltip,
  placeholder,
  charCount,
  maxChars = 50,
  loading = false,
  disabled = false,
  onKeyPress,
  fullWidth = true,
  error = false,
  helperText,
  sx,
  clearable = false,
}) => {
  // End adornment: spinner takes priority over clear button, then char counter
  const endAdornment = loading ? (
    <InputAdornment position="end">
      <CircularProgress size={16} sx={{ color: "#9AA6B8" }} />
    </InputAdornment>
  ) : clearable && searchQuery ? (
    <InputAdornment position="end">
      <IconButton
        size="small"
        aria-label="Clear search"
        title="Clear"
        onClick={() => onSearchChange({ target: { value: "" } })}
        disabled={disabled}
        sx={{ p: "4px", mr: "-6px" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ) : charCount !== undefined ? (
    <InputAdornment position="end">
      <Typography variant="caption" sx={{ fontSize: "12px", color: "#9AA6B8" }}>
        {charCount}/{maxChars}
      </Typography>
    </InputAdornment>
  ) : undefined;

  return (
    <Tooltip title={tooltip || ""} placement="top" arrow>
      <TextField
        size="small"
        autoComplete="off"
        fullWidth={fullWidth}
        value={searchQuery}
        onChange={(e) => onSearchChange(e)}
        onKeyPress={onKeyPress}
        disabled={disabled || loading}
        error={error}
        helperText={helperText}
        sx={[
          {
            borderRadius: "8px",
            width: "100%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px !important",
              height: 40,
              ...typography.bodyLarge,
              color: inputColor,
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: "8px",
                borderColor: borderColors.inputFieldGrey,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: ColorsConstants.primaryGray50,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: ColorsConstants.primaryGray100,
              },
            },
            "& .MuiOutlinedInput-input::placeholder": {
              color: inputColor,
              opacity: 1,
              ...typography.bodyLarge,
            },
          },
          sx,
        ]}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search color={inputColor} />
            </InputAdornment>
          ),
          ...(endAdornment && { endAdornment }),
          placeholder: placeholder || "Search",
        }}
      />
    </Tooltip>
  );
};

export default TableSearchInput;